import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './AccountSwitcher.css';

/**
 * Switch between the accounts belonging to one person.
 *
 * A staff member who is also a parent holds two accounts: one for their own children and one for
 * their job. This swaps between them in place - the district asked for single-page behaviour, no
 * second tab - so the header gains a control only for people who actually have a second account.
 *
 * Renders nothing at all for everyone else, which is almost everyone: on the 2026-09-14 data,
 * eleven people in the launch district could ever see this.
 */
const ROLE_LABEL = {
  parent: 'Parent',
  district_admin: 'District admin',
  district_editor: 'District editor',
  district_user: 'District user',
  superadmin: 'Platform admin'
};

const HOME_FOR = {
  parent: '/parent',
  district_admin: '/district-admin',
  district_editor: '/district-admin',
  district_user: '/district-admin',
  superadmin: '/superadmin'
};

const AccountSwitcher = () => {
  const { user, switchAccount } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/auth/linked-accounts');
      setAccounts(data.accounts || []);
    } catch {
      // A failure here must not break the page it sits in: the switcher is an extra, and
      // everyone without a second account sees nothing regardless.
      setAccounts([]);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user || accounts.length === 0) return null;

  const go = async (account) => {
    setBusy(true);
    setError('');
    const result = await switchAccount(account.id);
    setBusy(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    setOpen(false);

    /**
     * A full in-tab load of the destination, not a client-side route change.
     *
     * Switching capacity replaces the whole app shell, and a client-side navigate leaves the
     * previous dashboard mounted for a beat: its effects and pollers keep firing against the new
     * session and 403 (observed: enrollment-progress and school-lunch both rejected immediately
     * after a swap), and its route guard sees a role that no longer belongs on the route it is
     * still showing. Reloading in place is the only version of this that starts genuinely clean.
     *
     * Still single-page from the district's point of view: same tab, one click, no second
     * window.
     */
    window.location.assign(HOME_FOR[result.user.role] || '/');
  };

  return (
    <div className="account-switcher">
      <button
        type="button"
        className="account-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {ROLE_LABEL[user.role] || user.role}
        <span className="account-switcher-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <>
          <div className="account-switcher-scrim" onClick={() => setOpen(false)} />
          <div className="account-switcher-menu" role="menu">
            <p className="account-switcher-heading">Switch to</p>
            {accounts.map((a) => (
              <button
                key={a.id}
                type="button"
                role="menuitem"
                className="account-switcher-item"
                disabled={busy}
                onClick={() => go(a)}
              >
                <span className="account-switcher-role">{ROLE_LABEL[a.role] || a.role}</span>
                {a.district_name && (
                  <span className="account-switcher-sub">{a.district_name}</span>
                )}
              </button>
            ))}
            {error && <p className="account-switcher-error">{error}</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default AccountSwitcher;
